import { useState, useEffect, useMemo, useCallback } from 'react';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { getAccessibleFolders, appConfig } from '@/config/app-config';
import type { UserInfo, GroupFolderMapping } from '@/types';

// モックモードかどうか
const isMockMode = import.meta.env.VITE_APP_MOCK_MODE === 'true';

// モック用のユーザー情報
const mockUserInfo: UserInfo = {
  email: 'demo@example.com',
  groups: appConfig.groupFolderMapping.map(m => m.group), // 全グループにアクセス可能
};

/**
 * 認証・ユーザー情報管理フック
 */
export function useAuth() {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUserInfo = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const session = await fetchAuthSession();

      if (!session.tokens) {
        setUserInfo(null);
        return;
      }

      const idToken = session.tokens.idToken;
      const accessToken = session.tokens.accessToken;

      // メールアドレスを取得
      const email = (idToken?.payload?.email as string) || '';

      // グループ情報を取得（Access Tokenから）
      const groups = (accessToken?.payload?.['cognito:groups'] as string[]) || [];

      setUserInfo({
        email,
        groups,
      });
    } catch (err) {
      console.error('Failed to fetch user info:', err);
      setError('ユーザー情報の取得に失敗しました');
      setUserInfo(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isMockMode) {
      // モックモード: 即座にモックユーザー情報を設定
      setUserInfo(mockUserInfo);
      setLoading(false);
      return;
    }

    // 本番モード: Cognitoからユーザー情報を取得
    fetchUserInfo();

    // 認証イベント（ログイン・ログアウト・トークン更新）をリッスン
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
        case 'tokenRefresh':
          fetchUserInfo();
          break;
        case 'signedOut':
          setUserInfo(null);
          setLoading(false);
          break;
      }
    });

    return () => {
      unsubscribe();
    };
  }, [fetchUserInfo]);

  // アクセス可能なフォルダ一覧
  const accessibleFolders: GroupFolderMapping[] = useMemo(() => {
    if (!userInfo) return [];
    return getAccessibleFolders(userInfo.groups);
  }, [userInfo]);

  // ログアウト
  const handleSignOut = async () => {
    if (isMockMode) {
      alert('モックモードではログアウトできません');
      return;
    }

    try {
      await signOut();
      setUserInfo(null);
      window.location.href = '/';
    } catch (err) {
      console.error('Failed to sign out:', err);
    }
  };

  // 特定のグループに所属しているかチェック
  const hasGroup = (groupName: string): boolean => {
    return userInfo?.groups.includes(groupName) || false;
  };

  // いずれかのグループに所属しているかチェック
  const hasAnyGroup = (groupNames: string[]): boolean => {
    return groupNames.some(group => hasGroup(group));
  };

  return {
    userInfo,
    loading,
    error,
    accessibleFolders,
    handleSignOut,
    hasGroup,
    hasAnyGroup,
    refreshUserInfo: isMockMode ? () => Promise.resolve() : fetchUserInfo,
  };
}

export default useAuth;

